/**
 * AutoRenewsPanel — "Will this profile auto-charge?" at a glance.
 * Lists every auto-renew pack, legacy auto-renew credit, and active
 * subscription with its next-charge trigger + an ON/OFF control.
 * Owner spec 2026-08: "I don't want to auto charge something accidentally."
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCw, RefreshCwOff, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (cents) => (cents == null ? "—" : `$${(cents / 100).toFixed(2)}`);
const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function ToggleButton({ enabled, busy, onClick, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            disabled={busy}
            title={enabled ? "Click to turn OFF this auto-charge" : "Click to turn auto-renew back ON"}
            className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 border transition-colors disabled:opacity-50 whitespace-nowrap ${
                enabled
                    ? "border-emerald-700/60 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/50"
                    : "border-zinc-700 bg-zinc-900/40 text-zinc-400 hover:text-zinc-200"
            }`}
        >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : enabled ? <RefreshCw className="w-3 h-3" /> : <RefreshCwOff className="w-3 h-3" />}
            {enabled ? "ON" : "OFF"}
            <span className="normal-case tracking-normal text-[9px] opacity-70">· tap to turn {enabled ? "off" : "on"}</span>
        </button>
    );
}

function Row({ kind, label, trigger, amount, enabled, warning, children, testId }) {
    return (
        <div
            data-testid={testId}
            className={`flex items-center justify-between gap-3 border px-3 py-2.5 ${
                enabled ? "border-amber-700/40 bg-amber-950/10" : "border-zinc-900 bg-black/40"
            }`}
        >
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border border-zinc-700 text-zinc-400">{kind}</span>
                    <span className="text-sm text-white truncate">{label}</span>
                </div>
                <div className="text-[11px] text-zinc-400 mt-1">
                    <span className="font-mono text-zinc-200">{amount}</span> · {trigger}
                </div>
                {warning && (
                    <div className="text-[10px] text-rose-300 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Last renewal failed: {warning}
                    </div>
                )}
            </div>
            {children}
        </div>
    );
}

export default function AutoRenewsPanel({ userId }) {
    const [data, setData] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/coach/clients/${userId}/auto-renews`, { withCredentials: true });
            setData(r.data);
        } catch {
            setData(null);
        }
    }, [userId]);

    useEffect(() => {
        load();
        window.addEventListener("maxsc:lead-meta-refresh", load);
        return () => window.removeEventListener("maxsc:lead-meta-refresh", load);
    }, [load]);

    const togglePack = async (p) => {
        const next = !p.enabled;
        const msg = next
            ? `Turn auto-renew ON?\n\nWhen "${p.label}" runs out of sessions, their card will be auto-charged ${fmtMoney(p.renewal_price_cents)} + tax for a fresh pack.`
            : `Turn auto-renew OFF?\n\n"${p.label}" will run to completion, but NOTHING will be auto-charged when it runs out.`;
        if (!window.confirm(msg)) return;
        setBusyId(p.pack_id);
        try {
            await axios.post(`${API}/coach/clients/${userId}/packs/${p.pack_id}/auto-renew/toggle`, { enabled: next }, { withCredentials: true });
            toast.success(next ? "Auto-renew ON" : "Auto-renew OFF — no charge will fire");
            await load();
            window.dispatchEvent(new Event("maxsc:lead-meta-refresh"));
        } catch (e) {
            toast.error("Couldn't toggle auto-renew", { description: e?.response?.data?.detail || "Unknown error" });
        } finally { setBusyId(null); }
    };

    const toggleCredit = async (c) => {
        const next = !c.enabled;
        const msg = next
            ? `Turn auto-renew ON?\n\nWhen "${c.label}" runs out of sessions, their card will be auto-charged ${fmtMoney(c.renewal_price_cents)} + tax for a fresh pack.`
            : `Turn auto-renew OFF?\n\n"${c.label}" will run to completion, but NOTHING will be auto-charged when it runs out.`;
        if (!window.confirm(msg)) return;
        setBusyId(c.credit_id);
        try {
            await axios.post(`${API}/coach/clients/${userId}/credits/${c.credit_id}/auto-renew/toggle`, { auto_renew: next }, { withCredentials: true });
            toast.success(next ? "Auto-renew ON" : "Auto-renew OFF — no charge will fire");
            await load();
        } catch (e) {
            toast.error("Couldn't toggle auto-renew", { description: e?.response?.data?.detail || "Unknown error" });
        } finally { setBusyId(null); }
    };

    if (!data) return null;
    const { packs = [], credits = [], subscriptions = [], will_auto_charge } = data;
    const total = packs.length + credits.length + subscriptions.length;

    return (
        <div data-testid="auto-renews-panel" className="p-6 border-b border-zinc-900">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-1 flex items-center gap-2">
                <RefreshCw className="w-3 h-3" /> Upcoming auto-charges
                {will_auto_charge ? (
                    <span data-testid="auto-renews-live-badge" className="text-[9px] tracking-[0.2em] px-1.5 py-0.5 border border-amber-600/60 bg-amber-950/30 text-amber-200">WILL AUTO-CHARGE</span>
                ) : (
                    <span data-testid="auto-renews-safe-badge" className="text-[9px] tracking-[0.2em] px-1.5 py-0.5 border border-emerald-700/60 bg-emerald-950/30 text-emerald-200">NONE ACTIVE</span>
                )}
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">
                Everything on this profile that can bill the card automatically. Flip any of them OFF to stop the charge.
            </p>
            {total === 0 ? (
                <div data-testid="auto-renews-empty" className="border border-emerald-900/50 bg-emerald-950/10 px-3 py-2.5 text-xs text-emerald-200 flex items-center gap-2">
                    <ShieldCheck className="w-3.5 h-3.5" /> Nothing on this profile will auto-charge.
                </div>
            ) : (
                <div className="space-y-1.5">
                    {subscriptions.map((s) => (
                        <Row
                            key={s.subscription_id}
                            testId={`auto-renew-sub-row-${s.subscription_id}`}
                            kind="Subscription"
                            label={s.label}
                            amount={`${fmtMoney(s.amount_cents)}${s.interval ? `/${s.interval}` : ""}`}
                            trigger={s.will_renew ? `renews ${fmtDate(s.current_period_end)}` : `ends ${fmtDate(s.current_period_end)} — will NOT renew`}
                            enabled={s.will_renew}
                        >
                            <span className="text-[10px] text-zinc-500 whitespace-nowrap">manage in Subscriptions below</span>
                        </Row>
                    ))}
                    {packs.map((p) => (
                        <Row
                            key={p.pack_id}
                            testId={`auto-renew-pack-row-${p.pack_id}`}
                            kind={p.is_demo ? "Pack · demo" : "Pack renewal"}
                            label={p.label}
                            amount={`${fmtMoney(p.renewal_price_cents)} + tax`}
                            trigger={`charges when sessions run out · ${p.remaining_sessions} of ${p.sessions_total} left`}
                            enabled={p.enabled && !p.is_demo}
                            warning={p.last_renewal_failed_reason}
                        >
                            <ToggleButton
                                enabled={p.enabled}
                                busy={busyId === p.pack_id}
                                onClick={() => togglePack(p)}
                                testId={`auto-renew-pack-toggle-${p.pack_id}`}
                            />
                        </Row>
                    ))}
                    {credits.map((c) => (
                        <Row
                            key={c.credit_id}
                            testId={`auto-renew-credit-row-${c.credit_id}`}
                            kind="Pack renewal"
                            label={c.label}
                            amount={`${fmtMoney(c.renewal_price_cents)} + tax`}
                            trigger={`charges when sessions run out · ${c.remaining_sessions} of ${c.sessions_total} left`}
                            enabled={c.enabled}
                            warning={c.last_renewal_failed_reason}
                        >
                            <ToggleButton
                                enabled={c.enabled}
                                busy={busyId === c.credit_id}
                                onClick={() => toggleCredit(c)}
                                testId={`auto-renew-credit-toggle-${c.credit_id}`}
                            />
                        </Row>
                    ))}
                </div>
            )}
        </div>
    );
}
